import _ from "lodash";

/**
 * Converts a time string to seconds.
 * Supports formats: "XhYmZs", "XmYs", "Xs", "M:SS", or numeric values.
 */
export const convertTimeToSeconds = timeString => {
  if (_.isNumber(timeString)) {
    return timeString;
  }

  if (!_.isString(timeString)) {
    return 0;
  }

  const trimmedString = _.trim(timeString);

  // Check if format is "XhYmZs", "XmYs", or "Xs"
  if (
    _.includes(trimmedString, "h") ||
    _.includes(trimmedString, "m") ||
    _.includes(trimmedString, "s")
  ) {
    let totalSeconds = 0;

    const hoursMatch = trimmedString.match(/(\d+)h/);
    if (hoursMatch) {
      totalSeconds += parseInt(hoursMatch[1], 10) * 3600;
    }

    const minutesMatch = trimmedString.match(/(\d+)m/);
    if (minutesMatch) {
      totalSeconds += parseInt(minutesMatch[1], 10) * 60;
    }

    const secondsMatch = trimmedString.match(/(\d+)s/);
    if (secondsMatch) {
      totalSeconds += parseInt(secondsMatch[1], 10);
    }

    return totalSeconds;
  }

  // Handle "minutes:seconds" format (e.g., "3:23")
  const parts = _.split(trimmedString, ":");
  if (_.isEmpty(parts)) {
    return 0;
  }

  const minutes = parseInt(_.get(parts, 0, 0), 10);
  const seconds = parseInt(_.get(parts, 1, 0), 10);

  return minutes * 60 + seconds;
};

/**
 * Formats seconds to "M:SS" timestamp string.
 */
export const secondsToTimestamp = seconds => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Formats seconds to display time string.
 * Produces "H:MM:SS" for durations >= 1 hour, "M:SS" otherwise.
 */
export const formatTime = seconds => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const pad = num => _.padStart(num, 2, "0");

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(secs)}`
    : `${minutes}:${pad(secs)}`;
};

/**
 * Validates that a value is a valid "M:SS" timestamp.
 */
export const isValidTimestamp = value => {
  if (!value) return false;
  return /^\d+:\d{1,2}$/.test(String(value).trim());
};

/**
 * Parses a "M:SS" timestamp string to total seconds.
 * Returns null if invalid.
 */
export const parseTimestamp = value => {
  if (!isValidTimestamp(value)) return null;
  const parts = String(value).trim().split(":");
  const minutes = parseInt(parts[0], 10);
  const seconds = parseInt(parts[1], 10);
  if (seconds >= 60) return null;
  return minutes * 60 + seconds;
};
